import { fabric } from 'fabric';
import type { FabricEditor } from '../core/fabric-editor';
import type { EditorPlugin } from '../interfaces/editor-plugin';
import { FabricPluginName } from './plugin-name';

/**
 * The side the fill fades toward: the gradient is solid at the opposite edge and
 * fully transparent at the named side. `right` keeps the left edge solid and
 * fades out to the right, `down` keeps the top solid and fades out downward, etc.
 */
export type VignetteDirection = 'right' | 'left' | 'up' | 'down';

/**
 * Vignette ("fade-out") fill configuration, stored on `data.vignette`. The visible
 * `fill` gradient is derived from it (see `buildVignetteGradient`), so `data` stays
 * the single source of truth — both round-trip through `toJSON`/`loadFromJSON` and
 * undo/redo (Fabric serializes a gradient `fill` natively; `data` is in the history
 * include list).
 */
export interface VignetteConfig {
  /** Base ("monotone") color the gradient starts solid from, e.g. `#cccccc`. */
  color: string;
  /** Side the color fades toward. */
  direction: VignetteDirection;
  /** Where the fade begins, 0..1 along the gradient (0.5 = from the middle). */
  fadeStart: number;
}

export interface VignetteFacade {
  /**
   * Apply (or, with `undefined`, remove) a vignette on a fillable shape. With a
   * config: replaces `fill` with the derived gradient and writes `data.vignette`.
   * With `undefined`: restores a solid `fill` from the stored base color and clears
   * `data.vignette`. Records exactly one history step and repaints. Absence of
   * `data.vignette` is the deliberate "no vignette" tri-state (solid fill).
   */
  set(target: fabric.Object, config: VignetteConfig | undefined): void;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    vignette?: VignetteFacade;
  }
}

interface GradientCoords {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Gradient line per direction, in `percentage` units (0..1 fractions of the
 * object's bounding box, so the gradient scales with the shape on resize). The
 * line runs from the solid edge (offset 0) to the faded edge (offset 1).
 */
const COORDS_BY_DIRECTION: Record<VignetteDirection, GradientCoords> = {
  right: { x1: 0, y1: 0, x2: 1, y2: 0 },
  left: { x1: 1, y1: 0, x2: 0, y2: 0 },
  down: { x1: 0, y1: 0, x2: 0, y2: 1 },
  up: { x1: 0, y1: 1, x2: 0, y2: 0 },
};

const clamp01 = (v: number): number => Math.min(Math.max(v, 0), 1);

/**
 * Builds the linear gradient that materializes a vignette: solid base color from
 * offset 0 through `fadeStart`, then a smooth fade to fully transparent at offset
 * 1. `fabric.Color` parses the (hex) base color so the transparent stop reuses the
 * same RGB with alpha 0.
 */
export function buildVignetteGradient(config: VignetteConfig): fabric.Gradient {
  const [r, g, b] = new fabric.Color(config.color).getSource();
  const solid = `rgba(${r}, ${g}, ${b}, 1)`;
  const transparent = `rgba(${r}, ${g}, ${b}, 0)`;
  return new fabric.Gradient({
    type: 'linear',
    gradientUnits: 'percentage',
    coords: COORDS_BY_DIRECTION[config.direction],
    colorStops: [
      { offset: 0, color: solid },
      { offset: clamp01(config.fadeStart), color: solid },
      { offset: 1, color: transparent },
    ],
  });
}

/**
 * Adds a "vignette" gradient fill to fillable shapes (rect today; any
 * `fabric.Object` whose `fill` is painted).
 *
 * Unlike `rectCornersPlugin`, this plugin patches nothing — a gradient `fill` is
 * native to Fabric, so it renders and serializes with no prototype override. The
 * plugin is only a typed façade (`editor.vignette`) that builds the gradient on the
 * consumer's behalf (UI may not import `fabric` values) and keeps the visible
 * gradient and the persisted `data.vignette` config in lockstep.
 *
 * Façade `set` writes the new `fill` directly, then makes exactly one
 * `editor.objects.setData` call (the single `object:modified` emit) so the history
 * plugin records both the fill and the config change as one undo step.
 */
export function vignettePlugin(): EditorPlugin {
  return {
    name: FabricPluginName.VIGNETTE,
    install(editor: FabricEditor) {
      editor.vignette = {
        set(target: fabric.Object, config: VignetteConfig | undefined): void {
          if (config) {
            // Set the derived gradient directly (no emit) so the lone setData
            // below produces a single history step capturing fill + data.
            target.set('fill', buildVignetteGradient(config));
            target.dirty = true;
            editor.objects.setData(target, 'vignette', config);
          } else {
            // Restore a solid fill from the stored base color (read before the
            // config is cleared), falling back to the current string fill.
            const stored = (target.data as { vignette?: VignetteConfig } | undefined)?.vignette;
            const base = stored?.color ?? (typeof target.fill === 'string' ? target.fill : '#cccccc');
            target.set('fill', base);
            target.dirty = true;
            editor.objects.setData(target, 'vignette', undefined);
          }
        },
      };
    },
    dispose(editor: FabricEditor) {
      editor.vignette = undefined;
    },
  };
}
